"use client";

import { SearchTypes } from "@/declarations/search";
import Image from "next/image";
import Link from "next/link";
import CaretIcon from "public/next-public/common/icons/caret-regular-right.svg";
import React, { useState } from "react";
import type { Swiper as SwiperType } from "swiper";
import { FreeMode, Mousewheel, Scrollbar } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import styles from "./additionalSubCategories.module.scss";

export default function AdditionalSubCategoriesV2({
  parentCategoryName,
  additionalSubCategories = [],
  brandColor,
}: AdditionalSubCategoriesProps) {
  const [swiperInstance, setSwiperInstance] = useState<SwiperType | null>(null);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);

  if (additionalSubCategories.length === 0) return null;

  const syncNavState = (s: SwiperType) => {
    setIsBeginning(s.isBeginning);
    setIsEnd(s.isEnd);
  };

  const handlePrev = () => {
    if (!swiperInstance) return;
    const { slidesGrid, slidesSizesGrid, translate, width } = swiperInstance;
    const currentOffset = Math.abs(translate);
    // Walk backwards to find the last slide that starts before the current scroll
    // position — that is the partially clipped tile on the left.
    let targetIndex = 0;
    for (let i = slidesGrid.length - 1; i >= 0; i--) {
      if (slidesGrid[i] < currentOffset) {
        targetIndex = i;
        break;
      }
    }
    // Align so the target tile's right edge sits at the viewport's right edge,
    // making it the rightmost fully visible tile. Clamp to 0 (start of track).
    const targetTranslate = Math.min(0, -(slidesGrid[targetIndex] + slidesSizesGrid[targetIndex] - width));
    swiperInstance.translateTo(targetTranslate, 300, true);
    setIsBeginning(targetTranslate === 0);
  };

  const handleNext = () => {
    if (!swiperInstance) return;
    const { slidesGrid, slidesSizesGrid, translate, width } = swiperInstance;
    const rightEdge = Math.abs(translate) + width;
    // Find the first slide whose right edge extends beyond the visible area
    // — that is the partially clipped tile on the right.
    let targetIndex = slidesGrid.length - 1;
    for (let i = 0; i < slidesGrid.length; i++) {
      if (slidesGrid[i] + slidesSizesGrid[i] > rightEdge) {
        targetIndex = i;
        break;
      }
    }
    swiperInstance.slideTo(targetIndex);
  };

  return (
    <section
      className={styles["additional-sub-categories"]}
      aria-label={`Additional categories in ${parentCategoryName}`}
      style={brandColor ? ({ "--brand-color": brandColor } as React.CSSProperties) : undefined}
    >
      <p className={styles["carousel-title"]}>
        Additional Categories in <span>{parentCategoryName}</span>
      </p>

      <div className={styles["carousel-wrapper"]}>
        <Swiper
          modules={[FreeMode, Mousewheel, Scrollbar]}
          tag="div"
          wrapperTag="ul"
          slidesPerView="auto"
          spaceBetween={16}
          loop={false}
          freeMode={{ enabled: true, momentum: false }}
          mousewheel={{ forceToAxis: true }}
          scrollbar={{ hide: false, draggable: true, snapOnRelease: false }}
          onSwiper={(s) => {
            setSwiperInstance(s);
            syncNavState(s);
          }}
          onSlideChange={syncNavState}
          onReachEnd={syncNavState}
          onReachBeginning={syncNavState}
          onFromEdge={syncNavState}
          onTransitionEnd={syncNavState}
        >
          {additionalSubCategories.map((cat) => (
            <SwiperSlide key={cat.categoryId} tag="li" className={styles["carousel-slide"]}>
              <Link href={cat.categoryURI} className={styles["sub-category-tile"]} aria-label={cat.heading ?? cat.name}>
                <div className={styles["tile-image-wrapper"]}>
                  <Image
                    src={cat.imageURI}
                    fill
                    style={{ objectFit: "contain" }}
                    sizes="(max-width: 390px) calc(50vw - 10px), 160px"
                    alt={`Shop ${cat.name}`}
                  />
                </div>
                <span className={styles["tile-label"]} title={cat.heading ?? cat.name}>
                  {cat.heading ?? cat.name}
                </span>
              </Link>
            </SwiperSlide>
          ))}
        </Swiper>

        <button
          type="button"
          onClick={handlePrev}
          disabled={isBeginning}
          className={`${styles["carousel-arrow"]} ${styles["carousel-arrow-prev"]}${isBeginning ? ` ${styles["carousel-arrow-hidden"]}` : ""}`}
          aria-label="Scroll to previous categories"
        >
          <CaretIcon aria-hidden="true" focusable="false" style={{ transform: "scaleX(-1)" }} />
        </button>

        <button
          type="button"
          onClick={handleNext}
          disabled={isEnd}
          className={`${styles["carousel-arrow"]} ${styles["carousel-arrow-next"]}${isEnd ? ` ${styles["carousel-arrow-hidden"]}` : ""}`}
          aria-label="Scroll to next categories"
        >
          <CaretIcon aria-hidden="true" focusable="false" />
        </button>
      </div>
    </section>
  );
}

export type AdditionalSubCategoriesProps = {
  parentCategoryName: string;
  additionalSubCategories?: SearchTypes.TopSubCategory[];
  brandColor?: string;
};
